import React, { Dispatch, SetStateAction, useState } from 'react';
import { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { Button, Inform, Modal, Tooltip } from '@components/common';

import { isEmpty } from '@utils/common';

import { IOrganisationRoleInterface, TOrganizationType, TUserRoles } from '@enums/common';

import { MODAL_WIDTH_480 } from '@constants/modals';
import {
	noRolesAvailableTest,
	selectRolesText,
	warningInformText,
	warningInformTextBody,
} from './constants';
import { tabsNames } from '@components/ModalComponents/OrganisationForm/constants';

import RolesSection from './RolesSection';

import styles from './RoleAvailability.module.scss';

export interface IRoleAvailabilityProps {
	setCurrentTab: Dispatch<SetStateAction<string>>;
	isEditMode: boolean;
	submitForm: () => void;
	setValue: UseFormSetValue<FieldValues>;
	setIsDirtyFormModal: Dispatch<SetStateAction<boolean>>;
	watch: UseFormWatch<FieldValues>;
	organisationRoles: IOrganisationRoleInterface[];
	isWarningRolesModalOpened: boolean;
	setIsWarningRolesModalOpened: Dispatch<SetStateAction<boolean>>;
	prevRoleAvailability?: Array<TUserRoles>;
	types: TOrganizationType[];
}

const RoleAvailability = ({
	setCurrentTab,
	isEditMode,
	submitForm,
	setValue,
	setIsDirtyFormModal,
	watch,
	organisationRoles,
	isWarningRolesModalOpened,
	setIsWarningRolesModalOpened,
	prevRoleAvailability,
	types,
}: IRoleAvailabilityProps) => {
	const [isOpenWarningModal, setIsOpenWarningModal] = useState(false);
	const roleAvailability: Array<TUserRoles> = watch('roleAvailability') || [];

	const onClickBackHandler = () => setCurrentTab(tabsNames.organisationType);

	const toggleRole = (name: TUserRoles) => {
		const isRoleSelected = roleAvailability.includes(name);

		if (isRoleSelected) {
			const updatedRoles = roleAvailability.filter(role => role !== name);

			setValue('roleAvailability', updatedRoles);

			if (!isWarningRolesModalOpened && prevRoleAvailability?.includes(name)) {
				setIsOpenWarningModal(true);
				setIsWarningRolesModalOpened(true);
			}
		} else {
			setValue('roleAvailability', [...roleAvailability, name]);
		}

		setIsDirtyFormModal(true);
	};

	const closeWarningModal = () => setIsOpenWarningModal(false);

	return (
		<div className={styles.roleAvailabilityContainer}>
			<Modal
				isOpen={isOpenWarningModal}
				onClose={closeWarningModal}
				title='Warning'
				width={MODAL_WIDTH_480}
			>
				<Inform
					informText={warningInformText}
					onClose={closeWarningModal}
				>
					{warningInformTextBody}
				</Inform>
			</Modal>
			<div className={styles.title}>
				{isEmpty(types)
					? noRolesAvailableTest
					: selectRolesText
				}
			</div>
			{!isEmpty(types)
				&& <div className={styles.gridRoles}>
					{organisationRoles.map(category => {

						return (
							<RolesSection
								key={category.header}
								category={category}
								roleAvailability={roleAvailability}
								toggleRole={toggleRole}
							/>
						);
					})
					}
				</div>
			}
			{!isEditMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Tooltip
						isDisabled={!!types?.length}
						placement='top'
					>
						<div>
							<Button
								disabled={!types?.length}
								onClick={submitForm}
								text='Add'
							/>
						</div>
					</Tooltip>
				</div>
			}
		</div>
	);
};

export default RoleAvailability;
